import { useAuth } from "@clerk/clerk-react";
import { FormControlLabel, Grid, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import StyledCard from "components/Styled/Card";
import StyledSwitch from "components/Styled/Switch";
import StyledVaPage from "components/Styled/VAPage";
import { fetcherAuth } from "utils/api";
import { MODIFIED_EMAIL_WARMUP } from "utils/localStorage";
import { useTracking } from "utils/useTracking";

const SelfServeVaEmailWarmup = () => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);

  const { currentOrg, organizationVaProfile, setOrganizationVaProfile } =
    useContext(OrganizationUserContext);

  const { trackVaStep } = useTracking();
  const navigate = useNavigate();
  const hasModifiedEmailWarmup =
    localStorage.getItem(`${MODIFIED_EMAIL_WARMUP}-${currentOrg?.id}`) ===
    "true";

  const [emailWarmupEnabled, setEmailWarmupEnabled] = useState(
    hasModifiedEmailWarmup ? organizationVaProfile?.emailWarmupEnabled : true,
  );
  const [confirmWarningDialog, setConfirmWarningDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      if (!emailWarmupEnabled) {
        setConfirmWarningDialog(true);
        return;
      }
      goToNextStep(emailWarmupEnabled);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSkipWarmup = (emailWarmupEnabled: boolean) => {
    setConfirmWarningDialog(false);
    goToNextStep(emailWarmupEnabled);
  };

  const goToNextStep = async (emailWarmupEnabled: boolean) => {
    setLoading(true);
    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/va-settings`,
      "PUT",
      {},
      {
        emailWarmupEnabled,
      },
    );
    localStorage.setItem(`${MODIFIED_EMAIL_WARMUP}-${currentOrg?.id}`, "true");
    setOrganizationVaProfile((prev) => {
      if (prev) {
        return { ...prev, emailWarmupEnabled };
      }
      return prev;
    });
    navigate(`/${routes.reviewSelfServeOnboarding}`);
    trackVaStep(routes.reviewSelfServeOnboarding);
  };

  return (
    <>
      <StyledVaPage
        stepNumber={5}
        title="Want to warm up your email first?"
        description=""
        body={
          <StyledCard styles={{ px: 2, py: 4, mb: 4 }}>
            <Grid
              container
              alignItems="center"
              flexDirection={"column"}
              justifyContent="center"
            >
              <FormControlLabel
                sx={{ mb: 2 }}
                control={
                  <StyledSwitch
                    checked={emailWarmupEnabled}
                    onChange={(e) => setEmailWarmupEnabled(e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: { xs: 14, md: 18 }, ml: 1 }}>
                    {emailWarmupEnabled
                      ? "Yes, warm up my email"
                      : "Skip email warm up"}
                  </Typography>
                }
              />

              <Typography textAlign={"center"}>
                {!emailWarmupEnabled ? (
                  <>
                    ⚠️ Keep in mind: Skipping warm-up may increase the risk of
                    emails going to spam, especially with newer email accounts.
                  </>
                ) : (
                  <>
                    ✨ Bento AI will start by sending 50-60 emails per week,
                    then gradually increase to 100 pitches per week.
                  </>
                )}
              </Typography>
            </Grid>
          </StyledCard>
        }
        onBack={() => {
          trackVaStep(routes.preferencesSelfServeOnboarding);
          navigate(`/${routes.preferencesSelfServeOnboarding}`);
        }}
        buttonBack={"Go Back to Preferences"}
        buttonNext={"Complete Onboarding"}
        onNext={handleSubmit}
        loading={loading}
      />

      <ConfirmDialog
        open={confirmWarningDialog}
        header="Are you sure you want to skip email warmup?"
        subheader="Skipping warm-up may increase the risk of emails going to spam, especially with newer email accounts."
        handleClose={() => setConfirmWarningDialog(false)}
        handleConfirm={() => handleConfirmSkipWarmup(true)}
        handleCancel={() => handleConfirmSkipWarmup(false)}
        confirmText="Warm up my email"
        cancelText="Leave as is"
      />
    </>
  );
};

export default SelfServeVaEmailWarmup;
